import type { BrowserProvider } from 'ethers';
import type { ChainInfo } from '../contexts/NetworkContext';

interface TxErrorContext {
  provider?: BrowserProvider | null;
  account?: string | null;
  chain?: ChainInfo | null;
}

function shortAddr(a: string) {
  return `${a.slice(0, 6)}…${a.slice(-4)}`;
}

/**
 * Turn a MetaMask / ethers transaction error into a sentence a user can act on.
 *
 * The case that needs work is an empty wallet: ethers estimates gas before
 * sending, and on several chains an account with no balance makes that
 * estimate fail as a bare "missing revert data … CALL_EXCEPTION" instead of
 * "insufficient funds". So when gas estimation fails without a contract
 * reason, the wallet balance is read to tell the two apart.
 */
export async function describeTxError(e: unknown, ctx: TxErrorContext = {}): Promise<string> {
  const err = e as { code?: string; reason?: string | null; shortMessage?: string; message?: string };
  const msg = err?.message ?? String(e);
  const network = ctx.chain?.name ?? 'this network';
  const symbol = ctx.chain?.nativeCurrency?.symbol ?? 'native token';

  const noGas = () =>
    `Insufficient gas balance: your wallet${ctx.account ? ` (${shortAddr(ctx.account)})` : ''} doesn't have enough ` +
    `${symbol} on ${network} to pay the network fee. Add some ${symbol} to this wallet` +
    (ctx.chain?.faucet ? ` (a free test faucet is at ${ctx.chain.faucet})` : '') +
    `, or switch to a network where it has funds, then try again.`;

  if (err?.code === 'ACTION_REJECTED' || msg.includes('user rejected')) {
    return 'Transaction rejected in MetaMask. Nothing was changed.';
  }
  if (err?.code === 'INSUFFICIENT_FUNDS' || /insufficient funds/i.test(msg)) {
    return noGas();
  }
  // The contract gave a reason (e.g. "DIDRegistry: DID already exists").
  if (err?.reason) {
    return `The transaction was refused by the contract: ${err.reason.replace(/^[A-Za-z]+:\s*/, '')}.`;
  }
  if (err?.code === 'CALL_EXCEPTION' || err?.code === 'UNPREDICTABLE_GAS_LIMIT' || msg.includes('estimateGas')) {
    if (ctx.provider && ctx.account) {
      try {
        const balance = await ctx.provider.getBalance(ctx.account);
        if (balance === 0n) return noGas();
      } catch {
        // fall through to the generic message
      }
    }
    return (
      `The transaction could not be prepared on ${network}. Check that your wallet has enough ${symbol} ` +
      `for the network fee and that MetaMask is on ${network}, then try again.`
    );
  }
  if (err?.code === 'NETWORK_ERROR' || err?.code === 'TIMEOUT') {
    return `Could not reach ${network}. Check your connection and try again.`;
  }
  // Unknown: prefer ethers' one-line summary over the full payload dump.
  return err?.shortMessage ?? (msg.length > 200 ? `${msg.slice(0, 200)}…` : msg);
}
